'use client'

import * as React from 'react'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'

const Accordion = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('~border-b ~border-b-grey', className)}
    {...props}
  />
))
AccordionItem.displayName = 'AccordionItem'

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    headerClassName?: string
    iconClassName?: string
    /**
     * Heading tag Radix's Header renders as. Defaults to `h3` (Radix's own
     * default). Pass `h2` when the trigger is the top-level heading for its
     * section, so the page's heading hierarchy isn't silently downgraded.
     */
    headingLevel?: 'h2' | 'h3' | 'h4'
  }
>(
  (
    {
      className,
      headerClassName,
      iconClassName,
      headingLevel: Heading = 'h3',
      children,
      ...props
    },
    ref
  ) => (
    <AccordionPrimitive.Header asChild>
      <Heading className={cn('~flex ~mb-0', headerClassName)}>
        <AccordionPrimitive.Trigger
          ref={ref}
          className={cn(
            '~flex ~flex-1 ~items-center ~justify-between ~py-4 ~px-0 ~font-medium ~text-base ~font-gibson ~bg-transparent ~transition-all hover:~underline [&[data-state=open]>svg]:~rotate-180',
            className
          )}
          {...props}
        >
          {children}
          <ChevronDown
            className={cn(
              '~h-4 ~w-4 ~shrink-0 ~transition-transform ~duration-200',
              iconClassName
            )}
          />
        </AccordionPrimitive.Trigger>
      </Heading>
    </AccordionPrimitive.Header>
  )
)
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & {
    contentClassName?: string
  }
>(({ className, contentClassName, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      '~overflow-hidden ~text-sm ~transition-all data-[state=closed]:~animate-accordion-up data-[state=open]:~animate-accordion-down',
      contentClassName
    )}
    {...props}
  >
    <div className={cn('~pb-4 ~pt-0', className)}>{children}</div>
  </AccordionPrimitive.Content>
))

AccordionContent.displayName = AccordionPrimitive.Content.displayName

/**
 * @shared Used by:
 *   - ProviderDetails accordion sections (components/app/ProviderDetails/ProviderSectionContainer)
 *
 * Unlike `AccordionContent`, this variant always stays mounted in the DOM
 * (Radix `forceMount`) instead of unmounting on close, so collapsed content
 * remains present in the server-rendered HTML for SEO/AEO/LLM crawlers that
 * don't execute JS. The steady open/closed states are pure CSS
 * (`data-[state=open]:~h-auto` / `data-[state=closed]:~h-0`, already correct
 * in SSR since Radix sets `data-state` from the Accordion's `defaultValue`)
 * — JS only measures and animates the transition *between* those two states
 * when a real toggle happens, then hands height control back to CSS.
 * Coordinate changes with any other consumer.
 */
const AccordionSeoContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & {
    contentClassName?: string
  }
>(({ className, contentClassName, children, ...props }, forwardedRef) => {
  const rootRef = React.useRef<HTMLDivElement>(null)
  const innerRef = React.useRef<HTMLDivElement>(null)
  const [transitionHeight, setTransitionHeight] = React.useState<number | null>(
    null
  )

  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node

      if (typeof forwardedRef === 'function') {
        forwardedRef(node)
      } else if (forwardedRef) {
        forwardedRef.current = node
      }
    },
    [forwardedRef]
  )

  // Animate the transition between the two CSS-driven steady states
  // (data-[state=open]:h-auto / data-[state=closed]:h-0) whenever a real
  // toggle happens. The initial data-state (correct in SSR from Radix's
  // defaultValue) is intentionally left to CSS, so nothing here runs before
  // an actual user toggle. Also keeps the closed content `inert`: height:0
  // + overflow:hidden alone still leaves descendants focusable/tabbable,
  // so a keyboard user could tab into links inside a visually collapsed
  // section. `inert` removes them from focus/AT navigation without
  // affecting the DOM/HTML content itself, so it doesn't touch the
  // SEO/crawler guarantee (set via setAttribute, not the `.inert` IDL
  // property, since this project's TS lib doesn't type it yet).
  React.useEffect(() => {
    const node = rootRef.current

    if (!node) {
      return
    }

    const applyInert = (open: boolean) => {
      if (open) {
        node.removeAttribute('inert')
      } else {
        node.setAttribute('inert', '')
      }
    }

    let previousOpen = node.getAttribute('data-state') === 'open'
    applyInert(previousOpen)

    const handleAttributeChange = () => {
      const open = node.getAttribute('data-state') === 'open'

      if (open === previousOpen) {
        return
      }

      previousOpen = open
      applyInert(open)

      const measuredHeight = innerRef.current?.scrollHeight ?? 0

      if (open) {
        setTransitionHeight(0)
        requestAnimationFrame(() => setTransitionHeight(measuredHeight))
      } else {
        setTransitionHeight(measuredHeight)
        requestAnimationFrame(() => setTransitionHeight(0))
      }
    }

    const observer = new MutationObserver((mutations) => {
      if (
        mutations.some((mutation) => mutation.attributeName === 'data-state')
      ) {
        handleAttributeChange()
      }
    })
    observer.observe(node, {
      attributes: true,
      attributeFilter: ['data-state'],
    })

    return () => observer.disconnect()
  }, [])

  // Once a transition finishes, release the inline height back to
  // `undefined` so the matching CSS rule (h-auto when open, h-0 when
  // closed) takes back over, instead of leaving it pinned to the measured
  // snapshot. Releasing to `h-auto` after opening matters because nested
  // content (e.g. About's own "read more" toggle) can grow after this
  // section is already open — a frozen inline height would clip it.
  React.useEffect(() => {
    const node = rootRef.current

    if (!node || transitionHeight === null) {
      return
    }

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName === 'height') {
        setTransitionHeight(null)
      }
    }

    node.addEventListener('transitionend', handleTransitionEnd)
    return () => node.removeEventListener('transitionend', handleTransitionEnd)
  }, [transitionHeight])

  return (
    <AccordionPrimitive.Content
      ref={setRefs}
      forceMount
      className={cn(
        '~overflow-hidden ~transition-[height] ~duration-300 ~ease-out data-[state=open]:~h-auto data-[state=closed]:~h-0',
        contentClassName
      )}
      style={
        transitionHeight === null ? undefined : { height: transitionHeight }
      }
      {...props}
    >
      <div ref={innerRef} className={className}>
        {children}
      </div>
    </AccordionPrimitive.Content>
  )
})
AccordionSeoContent.displayName = 'AccordionSeoContent'

export {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  AccordionSeoContent,
}
